import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { apiGet, apiPost, clearSessionToken, clearOfflineCache } from '../lib/api';
import { authenticateWithPasskey } from '../lib/webauthn';
import { setDisplayPrefs } from '../lib/format';
import type { FxRates, Session } from '../lib/types';
import type { Cadence, PlanSelectionId } from '../lib/plans';
import { LEGAL_VERSIONS } from '../lib/legalVersions';

export interface PlanSelection {
  plan: PlanSelectionId;
  cadence: Cadence;
}

export type LoginResult =
  | { ok: true }
  | { ok: false; twoFactorRequired: true; challenge: string; reverify?: string }
  // An under-18 account whose parent or guardian has not approved it yet: the
  // password was right and no session exists (server/minor-accounts.mjs).
  | { ok: false; guardianPending: true; requestExpiresAt: string | null };

/** The under-18 signup path: the account waits for the guardian, so no session comes back. */
export interface MinorSignupInput {
  profile: { honorific: string; firstName: string; surname: string; email: string };
  password: string;
  inviteCode: string;
  minor: { dateOfBirth: string; guardianName: string; guardianHandle: string };
}

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  csrfToken: string | null;
  // An OBJECT, not positional args, and matching mobile/src/auth/AuthContext.tsx.
  // `inviteCode` is REQUIRED, so tsc fails on any caller that forgets it — a
  // positional 4th param after an optional 3rd could not be made required
  // (TS1016), which would have left the compiler unable to enforce the gate.
  signup: (input: {
    profile: { honorific: string; firstName: string; surname: string; email: string };
    password: string;
    inviteCode: string;
    /** DPDP G12 — REQUIRED and literally true: the server refuses anything else. */
    isAdult: true;
    plan?: PlanSelection;
  }) => Promise<void>;
  requestMinorSignup: (input: MinorSignupInput) => Promise<{ status: 'PENDING'; expiresAt: string }>;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTwoFactor: (challenge: string, code: string) => Promise<Session>;
  loginWithPasskey: (email: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const { session: current } = await apiGet<{ session: Session }>('/api/auth/me');
      setSession(current);
      // A live session invalidates any pending "signed out for inactivity"
      // notice (401 stragglers re-stamp the flag after AuthPage consumes it).
      try { sessionStorage.removeItem('sansara.sessionLost'); } catch { /* storage unavailable */ }
      // F43/F124: apply the account's display currency + locale immediately so
      // all formatting matches the user's preference across the app.
      setDisplayPrefs({ currency: current.preferences.displayCurrency, locale: current.preferences.locale });
      // Refresh FX (display-only) in the background; ignore failures.
      if (current.twoFactorEnrolled) {
        apiGet<FxRates>('/api/fx/rates')
          .then((fx) => setDisplayPrefs({ fx: fx.rates }))
          .catch(() => {});
      }
    } catch {
      setSession(null);
    }
  }, []);

  useEffect(() => {
    refreshSession().finally(() => setLoading(false));
  }, [refreshSession]);

  // When any API call is blocked by the mandatory-2FA gate, re-sync the session
  // so route guards send the user to the enrollment screen.
  useEffect(() => {
    const onTwoFactorRequired = () => {
      refreshSession();
    };
    window.addEventListener('sansara:two-factor-required', onTwoFactorRequired);
    return () => window.removeEventListener('sansara:two-factor-required', onTwoFactorRequired);
  }, [refreshSession]);

  // Conv-26: a data-route 401 means the session died server-side (idle
  // timeout, revoke, expiry). Clear the local session so route guards bounce
  // to sign-in — api.ts already stamped sessionStorage so the sign-in page
  // can say WHY ("signed out after inactivity") instead of looking broken.
  useEffect(() => {
    const onSessionLost = () => {
      // Same hygiene as a deliberate logout: the idle timeout exists for the
      // walk-away/shared-browser case, so the cached financial figures must
      // die with the session (diff-verify caught the automatic path skipping
      // the clear that logout() does on purpose).
      clearOfflineCache();
      setSession((s) => (s ? null : s));
    };
    window.addEventListener('sansara:session-lost', onSessionLost);
    return () => window.removeEventListener('sansara:session-lost', onSessionLost);
  }, []);

  const signup = useCallback(
    async ({
      profile,
      password,
      inviteCode,
      isAdult,
      plan,
    }: {
      profile: { honorific: string; firstName: string; surname: string; email: string };
      password: string;
      inviteCode: string;
      isAdult: true;
      plan?: PlanSelection;
    }) => {
      // Conv-56: every account starts on the FREE plan, server-side — the trial
      // is gone. The pick is recorded as a planSelection ("selected, not yet
      // billed") and takes effect at the checkout whenever the user buys.
      // `subscriptionTier` is deliberately NOT sent: the server ignores it, and
      // sending it would keep alive the idea that the client picks a starting tier.
      const planSelection = plan ? { tier: plan.plan, cadence: plan.cadence } : null;
      const { session: next } = await apiPost<{ session: Session }>('/api/auth/signup', {
        profile,
        password,
        inviteCode,
        isAdult,
        // DPDP G4: the document versions this page showed; the server records
        // them and refuses (409 POLICY_CHANGED) if they are out of date.
        policyVersions: LEGAL_VERSIONS,
        planSelection,
      });
      setSession(next);
    },
    [],
  );

  const requestMinorSignup = useCallback(async (input: MinorSignupInput) => {
    const { minorRequest } = await apiPost<{ minorRequest: { status: 'PENDING'; expiresAt: string } }>('/api/auth/signup', {
      ...input,
      policyVersions: LEGAL_VERSIONS,
    });
    return minorRequest;
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const res = await apiPost<{ session?: Session; twoFactorRequired?: boolean; challenge?: string; guardianApprovalPending?: boolean; requestExpiresAt?: string | null }>(
      '/api/auth/login',
      { email, password },
    );
    if (res.guardianApprovalPending) return { ok: false, guardianPending: true, requestExpiresAt: res.requestExpiresAt ?? null };
    if (res.twoFactorRequired && res.challenge) {
      return { ok: false, twoFactorRequired: true, challenge: res.challenge };
    }
    if (res.session) setSession(res.session);
    return { ok: true };
  }, []);

  const verifyTwoFactor = useCallback(async (challenge: string, code: string) => {
    const { session: next } = await apiPost<{ session: Session }>('/api/auth/2fa/verify-login', { challenge, code });
    setSession(next);
    return next;
  }, []);

  // Passwordless sign-in with a passkey (possession + enforced user-verification
  // = both factors in one step). TOTP remains the fallback.
  const loginWithPasskey = useCallback(async (email: string): Promise<LoginResult> => {
    const res = await authenticateWithPasskey(email);
    if ('session' in res) {
      setSession(res.session);
      return { ok: true };
    }
    return { ok: false, twoFactorRequired: true, challenge: res.challenge, reverify: res.reverify };
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost('/api/auth/logout');
    } finally {
      clearSessionToken();
      // Wipe any financial data cached for offline viewing so it cannot be read
      // by the next person on a shared browser after sign-out.
      clearOfflineCache();
      setSession(null);
    }
  }, []);

  const value = useMemo(
    () => ({ session, loading, csrfToken: session?.csrfToken ?? null, signup, requestMinorSignup, login, verifyTwoFactor, loginWithPasskey, logout, refreshSession }),
    [session, loading, signup, requestMinorSignup, login, verifyTwoFactor, loginWithPasskey, logout, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
